import React, { useMemo } from 'react';
import {
  addDays,
  addSeconds,
  isWithinInterval,
  max,
  min,
  parseISO,
} from 'date-fns';

import { Flex } from '@reckon-web/flex';
import { XIcon } from '@reckon-web/icon';
import { IconButton } from '@reckon-web/button';
import { Divider } from '@reckon-web/divider';
import { useTheme } from '@reckon-web/theme';
import { Notice } from '@reckon-web/notice';
import { makeId } from '@reckon-web/utils';

import { Task } from '../../hooks/use-query-tasks';
import { useExportToCsv } from '../../hooks/use-export-to-csv';
import { useVisibleProperties } from '../common/visible-properties';
import { useVisibleFilters } from '../common/visible-filters';
import { DataControls } from '../work/data-controls';
import { KanbanOrList } from '../work/kanban-or-list';
import { SortType } from '../work/sort';
import {
  JobTasksFilter,
  emptyJobTasksFilter,
  equals,
  getActiveJobTasksFilter,
} from '../work/filter-helpers';
import { tasksToCsv } from '../work/export-helpers';
import {
  TaskStatusEnum,
  TaskStatusEnumValue,
} from '../work/types/task-status-enum';
import { useGetMyDetails } from '../../data/useGetMyDetails';
import {
  GetJobStatusListResult,
  useGetJobStatusList,
} from '../../data/useGetJobStatusList';

import { Filter } from './tasks/filter';
import { Tasks } from './tasks/Tasks';
import { New, NewCloseArgs } from './new';
import { Opts } from './new-job-dialog';
import { JobStatusEnum } from './types/job-status-enum';

export type ExportableJobDetails = {
  jobName?: string;
  debtorName?: string;
};

export const NewJob: React.FC<{
  onClose: (args: NewCloseArgs) => void;
  opts?: Opts;
  setShouldPromptUnsaved: (value: boolean) => void;
  testId?: string;
}> = ({ onClose, opts, setShouldPromptUnsaved, testId = `new-job` }) => {
  const [visibleProperties] = useVisibleProperties();
  const [visibleFilters] = useVisibleFilters();
  const { data: currentUser } = useGetMyDetails();
  const statuses = useGetJobStatusList();
  const jobStatusFromOpts = useMemo(() => {
    let result: GetJobStatusListResult | undefined;
    if (opts?.mode === 'create' && opts.statusId) {
      result = statuses.data?.find((x) => x.cloudId === opts.statusId);
    }
    if (!result) {
      result = statuses.data?.find(
        (x) => x.status === JobStatusEnum.valuesEnum.NotStarted
      );
    }
    return result;
  }, [opts, statuses]);

  const [ExportToCsvDialog, _exportToCsv] = useExportToCsv();

  const [kanbanOrList, setKanbanOrList] =
    React.useState<KanbanOrList>('kanban');
  const [sort, setSort] = React.useState<SortType>('Due Date (earliest first)');

  const [filter, _setFilter] =
    React.useState<JobTasksFilter>(emptyJobTasksFilter);
  const setFilter = (value: Partial<JobTasksFilter>) =>
    _setFilter((prev) => ({ ...prev, ...value }));
  const clearFilter = () => _setFilter(emptyJobTasksFilter);

  const activeFilter = React.useMemo(
    () => getActiveJobTasksFilter(filter, visibleFilters),
    [filter, visibleFilters]
  );
  const filtered = React.useMemo(
    () => !equals(activeFilter, emptyJobTasksFilter),
    [activeFilter]
  );

  const [tasks, setTasks] = React.useState<Task[]>();

  const matchingTasks = React.useMemo(
    () => filterTasks(tasks, activeFilter, currentUser?.user?.cloudId || ''),
    [tasks, activeFilter, currentUser?.user]
  );

  const [exportableJobDetails, setExportableJobDetails] =
    React.useState<ExportableJobDetails>();

  const exportToCsv = React.useCallback(() => {
    if (matchingTasks && jobStatusFromOpts) {
      _exportToCsv({
        exportWorkType: 'task',
        count: matchingTasks.length,
        csv: tasksToCsv(
          (() => {
            switch (kanbanOrList) {
              case 'kanban':
                return visibleProperties.taskBoard;
              case 'list':
                return visibleProperties.taskList;
            }
          })()
        )(
          matchingTasks.map((t) => ({
            ...t,
            job: {
              cloudId: '',
              client: {
                cloudId: '',
                tenantId: '',
                isDeleted: false,
                name: exportableJobDetails?.debtorName || '',
                endDateUtc: '',
              },
              name: exportableJobDetails?.jobName || '',
              status: jobStatusFromOpts,
              WfDeletedOnUTC: '',
              clientId: '',
              jobStatusId: '',
            },
          }))
        ),
      });
    }
  }, [
    visibleProperties,
    _exportToCsv,
    kanbanOrList,
    matchingTasks,
    exportableJobDetails,
    jobStatusFromOpts,
  ]);

  const theme = useTheme();

  return (
    <React.Fragment>
      <Flex height="100%" data={{ testid: testId }}>
        <Flex width="360px" flexShrink={0}>
          <New
            onClose={onClose}
            opts={opts}
            tasks={tasks}
            setTasks={setTasks}
            setShouldPromptUnsaved={setShouldPromptUnsaved}
            setExportableJobDetails={setExportableJobDetails}
          />
        </Flex>
        <Divider vertical />
        <Flex
          flexGrow={1}
          overflow="auto"
          direction="column"
          background="muted"
        >
          <Flex gap="small" paddingX="xlarge" flexShrink={0}>
            <Flex paddingY="large">
              <Filter
                filter={filter}
                setFilter={setFilter}
                clearFilter={clearFilter}
                filtered={filtered}
                tasks={tasks}
              />
            </Flex>
            <Flex flexGrow={1} />
            <Divider vertical />
            <Flex paddingLeft="small" paddingY="large" gap="medium">
              <DataControls
                jobsOrTasks="tasks"
                kanbanOrList={kanbanOrList}
                setKanbanOrList={setKanbanOrList}
                sort={sort}
                setSort={setSort}
                exportToCsv={exportToCsv}
                canExport={Boolean(matchingTasks?.length)}
              />
              <IconButton
                icon={XIcon}
                label="Exit dialog"
                tone="passive"
                weight="none"
                size="small"
                onClick={() => onClose({ didCancel: true })}
                css={{ marginRight: `-${theme.spacing.small}px` }}
                data-testid={makeId(testId, 'exit')}
              />
            </Flex>
          </Flex>
          <Divider />
          {(tasks?.length || 0) > 0 && (
            <Flex
              flexGrow={0}
              flexShrink={0}
              paddingX="xlarge"
              paddingTop="large"
            >
              <Notice size="small" data-testid={makeId(testId, 'notice')}>
                Note: Tasks cannot be edited until you finish creating this job.
              </Notice>
            </Flex>
          )}
          <Flex
            direction="column"
            flexGrow={1}
            paddingX="xlarge"
            paddingBottom="large"
            overflow="auto"
          >
            <Tasks
              kanbanOrList={kanbanOrList}
              queryResult={{ data: matchingTasks }}
              filtered={filtered}
              clearFilter={clearFilter}
              sort={sort}
              readonly={true}
            />
          </Flex>
        </Flex>
      </Flex>
      <ExportToCsvDialog />
    </React.Fragment>
  );
};

// HACK this belongs somewhere else. Don't know where though
export function filterTasks(
  tasks: Task[] | undefined,
  filter: JobTasksFilter,
  currentUserCloudId: string | undefined
) {
  if (!tasks || !tasks.length) {
    return tasks;
  }

  const now = new Date();

  const { start: startDueDate, end: endDueDate } =
    parseDateSelectionsForRangeSearch(filter.dueDateBegin, filter.dueDateEnd);
  const { start: startCompletedDate, end: endCompletedDate } =
    parseDateSelectionsForRangeSearch(
      filter.completedDateBegin,
      filter.completedDateEnd
    );

  const doneStatuses = new Set<TaskStatusEnumValue>([
    TaskStatusEnum.valuesEnum.Done,
    TaskStatusEnum.valuesEnum.Cancelled,
  ]);
  const selectedStatusValues = new Set(filter.statuses);
  const selectedEntityIds = new Set(filter.entities);
  const selectedFamilyGroupIds = new Set(filter.familyGroupIds);
  const selectedStaffIds = new Set(filter.staff);
  const textSearch = filter.text
    ? new RegExp(filter.text, 'i')
    : { test: (val: string) => true };

  const results = tasks.filter((task) => {
    let containsEntities = true;
    let containsFamilyGroups = true;
    let containsStaff = true;
    let containsStatus = true;
    let containsSearch = true;
    let inDueDateRange = true;
    let inCompletedDateRange = true;
    if (filter.entities?.length) {
      if (task.entity) {
        containsEntities = selectedEntityIds.has(task.entity.cloudId);
      } else {
        containsEntities = false;
      }
    }
    if (filter.familyGroupIds?.length) {
      if (task.entity && task.entity.familyGroupId) {
        containsFamilyGroups = selectedFamilyGroupIds.has(
          task.entity.familyGroupId
        );
      } else {
        containsFamilyGroups = false;
      }
    }
    if (filter.myWork) {
      if (task.assignee) {
        containsStaff = task.assignee.cloudId === currentUserCloudId;
      } else {
        containsStaff = false;
      }
    } else if (!filter.myWork && filter.staff?.length) {
      if (task.assignee) {
        containsStaff = selectedStaffIds.has(task.assignee.cloudId || '');
      } else {
        containsStaff = false;
      }
    }
    if (filter.statuses?.length && task.status) {
      containsStatus = selectedStatusValues.has(task.status);
    }

    containsSearch = textSearch.test(task.name || '');

    if (filter.dueDateOverdue) {
      if (task.dueDate) {
        // if they want overdue tasks, gotta check the date AND make sure the task isn't done. Done/cancelled tasks are never overdue
        inDueDateRange =
          task.dueDate < now &&
          !doneStatuses.has(
            task.status || TaskStatusEnum.valuesEnum.NotStarted
          );
      } else {
        inDueDateRange = false;
      }
    } else if (startDueDate && endDueDate) {
      if (task.dueDate) {
        inDueDateRange = isWithinInterval(task.dueDate, {
          start: startDueDate,
          end: endDueDate,
        });
      } else {
        inDueDateRange = false;
      }
    }
    if (startCompletedDate && endCompletedDate) {
      if (task.completedDate) {
        inCompletedDateRange = isWithinInterval(task.completedDate, {
          start: startCompletedDate,
          end: endCompletedDate,
        });
      } else {
        inCompletedDateRange = false;
      }
    }
    return (
      containsEntities &&
      containsFamilyGroups &&
      containsStaff &&
      containsStatus &&
      containsSearch &&
      inDueDateRange &&
      inCompletedDateRange
    );
  });
  return results;
}

function parseDateSelectionsForRangeSearch(
  start: string | undefined,
  end: string | undefined
) {
  const result: { start: Date | undefined; end: Date | undefined } = {
    start: undefined,
    end: undefined,
  };

  // if they have no selection, we're done
  if (!start || !end) {
    return result;
  }

  // parse up the first date. Duh
  const firstParsed = parseISO(start);
  // If they've only picked one date, pretend like it is both the start and end date
  const secondParsed = parseISO(end || start);

  // In the vue app there was nothing to say that the dates are in the array in the correct order
  // In React I think they are, but just in case
  const minSelection = min([firstParsed, secondParsed]);
  const maxSelection = max([firstParsed, secondParsed]);

  // Then, if they select start 25th, end 26th, we want to search for >= 25th && < 27th
  // ie: we want start to be inclusive, end to be exclusive,
  //   and we want to search for one day more than they selected for the end
  // BUT, isWithinInterval is inclusive of both ends
  // so, we need to subtract a second from the end date to make it exclusive
  // I think this is safe for leap seconds
  result.start = minSelection;
  result.end = addSeconds(addDays(maxSelection, 1), -1);

  return result;
}
